/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import PaymentMethodCard from "./PaymentMethodCard";
import PaymentMethodModal from "./PaymentMethodModal";
import { mapPaymentMethodDataStatus } from "../../../assets/helper.js";
import { simpleFetch } from "../../../restWrapper.js";
import RenderPaymentCode from "./RenderPaymentCode";
import RenderStripe from "./RenderStripe.js";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import "../RenderPaymentMethods.css";

// Import full-page payment method components for modal reuse
import { RenderAlipay } from "./RenderAlipay.js";
import { RenderWeChatPay } from "./RenderWeChatPay.js";
import { RenderBocPay } from "./RenderBocPay.js";
import { RenderOctopus } from "./RenderOctopus.js";
import RenderFPS from "./RenderFPS.js";
import RenderBankTransfer from "./RenderBankTransfer.js";
import RenderCheckWriting from "./RenderCheckWriting.js";
import RenderCash from "./RenderCash.js";
import RenderPayPalManual from "./RenderPayPalManual.js";

// Payment methods configuration
const PAYMENT_METHODS_CONFIG = {
  automatic: [
    { key: "stripe", label: "Stripe", logoClass: "stripe-logo", requiresPlan: null },
    { key: "payme", label: "PayMe", logoClass: "payme-logo", requiresPlan: "pro" },
    { key: "paypal", label: "PayPal", logoClass: "paypal-logo", requiresPlan: "pro" },
    { key: "tapandgo", label: "Tap & Go", logoClass: "tapandgo-logo", requiresPlan: "premium" }
  ],
  manual: [
    {
      key: "alipay",
      label: "Alipay",
      logoClass: "alipay-logo",
      requiresPlan: "premium",
      subMethods: [
        { key: "alipay_hk", label: "Hong Kong" },
        { key: "alipay_ch", label: "China" }
      ]
    },
    {
      key: "wechatpay",
      label: "WeChat Pay",
      logoClass: "wechatpay-logo",
      requiresPlan: "premium",
      subMethods: [
        { key: "wechatpay_hk", label: "Hong Kong" },
        { key: "wechatpay_ch", label: "China" }
      ]
    },
    { key: "bocpay", label: "BoC Pay", logoClass: "bocpay-logo", requiresPlan: "premium" },
    { key: "octopus", label: "Octopus", logoClass: "octopus-logo", requiresPlan: "premium" },
    { key: "fps", label: "FPS", logoClass: "fps-logo", requiresPlan: "premium" },
    { key: "bank_transfer", label: "Bank Transfer", logoClass: "banktransfer-logo", requiresPlan: "premium" },
    { key: "check_writing", label: "Cheque", logoClass: "cheque-logo", requiresPlan: "premium" },
    { key: "cash", label: "Cash", logoClass: "cash-logo", requiresPlan: "premium" },
    { key: "paypal_manual", label: "PayPal", logoClass: "paypal-logo", requiresPlan: "premium" }
  ]
};

class RenderPaymentMethods extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalOpen: false,
      selectedMethod: null,
      modalFormData: {},
      isEditMode: false,
      modalSaving: false,
      modalError: '',
      savingRequireTransactionId: false,
      savingFeeCoverageMode: false,
      feeCoverageModeStatus: '',
      savingShowFeeCoverageAmount: false,
    };
    this.childComponentRef = React.createRef();
  }

  getMethodStatus(config) {
    const { state } = this.props;
    const orgObj = state.organizationObj || {};
    const paymentMethodCode = orgObj.paymentMethodCode || {};
    const currentPlan = (orgObj.currentPlan || "").toLowerCase();
    const isPremiumOrPro = currentPlan === "premium" || currentPlan === "pro";

    // Check if method requires upgrade
    let requiresUpgrade = false;
    if (config.requiresPlan) {
      if (config.requiresPlan === "premium" && currentPlan !== "premium") {
        requiresUpgrade = true;
      } else if (config.requiresPlan === "pro" && !isPremiumOrPro) {
        requiresUpgrade = true;
      }
    }

    // Special handling for different payment methods
    let isEnabled = false;
    let hasData = false;
    let isConfigured = false;
    let isLoading = false;

    // For Stripe - check bank accounts and KYC status
    let actionRequired = false;
    if (config.key === "stripe") {
      // Check if still loading
      if (state.bankAc === undefined || state.stripeKyc === undefined) {
        isLoading = true;
      } else {
        hasData = state.bankAc.length > 0;
        isConfigured = hasData;
        // Stripe is enabled only if has bank accounts AND KYC is complete (stripeKyc === 0)
        isEnabled = hasData && state.stripeKyc === 0;
        // Action required if has bank accounts but KYC is incomplete
        actionRequired = hasData && state.stripeKyc > 0;
      }
    }
    // For PayMe - check organizationObj fields
    else if (config.key === "payme") {
      // Check if organizationObj is loaded
      if (!orgObj || Object.keys(orgObj).length === 0) {
        isLoading = true;
      } else {
        hasData = Boolean(orgObj.clientId);
        isEnabled = hasData;
        isConfigured = hasData;
      }
    }
    // For PayPal - not fully implemented yet
    else if (config.key === "paypal") {
      hasData = false;
      isEnabled = false;
      isConfigured = false;
    }
    // For Tap & Go - check paymentMethodCode
    else if (config.key === "tapandgo") {
      const status = mapPaymentMethodDataStatus(paymentMethodCode, "tapandgo");
      hasData = status.show;
      isEnabled = status.checked;
      isConfigured = status.show;
    }
    // For methods with sub-methods (Alipay, WeChat Pay)
    else if (config.subMethods) {
      // Check if paymentMethodCode is loaded
      if (!orgObj || Object.keys(orgObj).length === 0) {
        isLoading = true;
      } else {
        config.subMethods.forEach((sub) => {
          const status = mapPaymentMethodDataStatus(paymentMethodCode, sub.key);
          if (status.show) hasData = true;
          if (status.checked) isEnabled = true;
        });
        isConfigured = hasData;
      }
    }
    // For other standard payment methods
    else {
      // Check if paymentMethodCode is loaded
      if (!orgObj || Object.keys(orgObj).length === 0) {
        isLoading = true;
      } else {
        const status = mapPaymentMethodDataStatus(paymentMethodCode, config.key);
        hasData = status.show;
        isEnabled = status.checked;
        isConfigured = status.show;
      }
    }

    return { isEnabled, hasData, isConfigured, requiresUpgrade, isLoading, actionRequired };
  }

  isMobileDevice = () => {
    return window.innerWidth <= 768;
  };

  handleOpenModal = (methodConfig) => {
    // On mobile, go directly to expanded page view
    if (this.isMobileDevice()) {
      this.handleExpandToPage(methodConfig.key);
      return;
    }

    const formData = this.getFormDataForMethod(methodConfig);
    this.setState({
      modalOpen: true,
      selectedMethod: methodConfig,
      modalFormData: formData
    });
  };

  handleCloseModal = () => {
    this.setState({
      modalOpen: false,
      selectedMethod: null,
      modalFormData: {},
      isEditMode: false,
      modalSaving: false,
      modalError: ''
    });
  };

  handleModalEdit = () => {
    if (this.childComponentRef.current?.handleToggleEditMode) {
      this.childComponentRef.current.handleToggleEditMode();
    }
    this.setState({ isEditMode: true });
  };

  handleModalSave = () => {
    if (this.childComponentRef.current?.handleSave) {
      this.childComponentRef.current.handleSave();
    }
    this.setState({ isEditMode: false });
  };

  handleModalCancel = () => {
    if (this.childComponentRef.current?.handleToggleEditMode) {
      this.childComponentRef.current.handleToggleEditMode();
    }
    this.setState({ isEditMode: false });
  };

  handleExpandToPage = (methodKey) => {
    // Map method key to page index
    const methodToPageIndex = {
      stripe: 100,
      payme: 101,
      paypal: 102,
      tapandgo: 103,
      alipay: 104,
      wechatpay: 105,
      bocpay: 106,
      octopus: 107,
      fps: 108,
      bank_transfer: 109,
      check_writing: 110,
      cash: 111,
      paypal_manual: 112
    };

    const pageIndex = methodToPageIndex[methodKey];
    if (pageIndex && this.props.func.clickChangePage) {
      this.handleCloseModal();
      this.props.func.clickChangePage(pageIndex);
    }
  };

  getFormDataForMethod(methodConfig) {
    const { state } = this.props;
    const orgObj = state.organizationObj || {};
    const paymentMethodCode = orgObj.paymentMethodCode || {};

    switch (methodConfig.key) {
      case "payme":
        return {
          clientId: orgObj.clientId || "",
          clientSecret: orgObj.clientSecret || "",
          signingKeyId: orgObj.signingKeyId || "",
          signingKey: orgObj.signingKey || "",
          paymeTransactionFeePercentage: orgObj.paymeTransactionFeePercentage || ""
        };

      case "fps": {
        const fpsData = paymentMethodCode.fps || {};
        return {
          fpsIdentifier: fpsData.fpsIdentifier || "",
          fpsEmailAddress: fpsData.fpsEmailAddress || "",
          fpsPhoneNumber: fpsData.fpsPhoneNumber || "",
          fpsBankCode: fpsData.fpsBankCode || "",
          fpsBankBranchCode: fpsData.fpsBankBranchCode || "",
          fpsBankAccountName: fpsData.fpsBankAccountName || "",
          fpsBankAccountNumber: fpsData.fpsBankAccountNumber || ""
        };
      }

      case "bank_transfer": {
        const btData = paymentMethodCode.bank_transfer || {};
        return {
          bankTransferCode: btData.bankTransferCode || "",
          bankTransferBranchCode: btData.bankTransferBranchCode || "",
          bankTransferAccountName: btData.bankTransferAccountName || "",
          bankTransferAccountNumber: btData.bankTransferAccountNumber || ""
        };
      }

      case "check_writing": {
        const checkData = paymentMethodCode.check_writing || {};
        return {
          checkWritingOrgName: checkData.checkWritingOrgName || "",
          checkWritingInstructions: checkData.checkWritingInstructions || ""
        };
      }

      case "cash": {
        const cashData = paymentMethodCode.cash || {};
        return {
          cashOrgName: cashData.cashOrgName || "",
          cashInstructions: cashData.cashInstructions || ""
        };
      }

      case "paypal_manual": {
        const paypalManualData = paymentMethodCode.paypal_manual || {};
        return {
          paypalManualOrgName: paypalManualData.paypalManualOrgName || "",
          paypalManualInstructions: paypalManualData.paypalManualInstructions || "",
          paypalManualLink: paypalManualData.paypalManualLink || ""
        };
      }

      default:
        return paymentMethodCode[methodConfig.key] || {};
    }
  }

  handleFormChange = (updates) => {
    this.setState((prevState) => ({
      modalFormData: {
        ...prevState.modalFormData,
        ...updates
      }
    }));
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.handleFormChange({ [name]: value });
  };

  // Legacy save handler for PayMe modal form
  handlePayMeSave = async () => {
    const { modalFormData, modalSaving } = this.state;
    const { state, func, languageObj } = this.props;
    const textObj = languageObj?.PaymentMethods || {};
    const saveFailedMsg = textObj.saveFailed || "Failed to save. Please try again.";

    if (modalSaving) return;
    this.setState({ modalSaving: true, modalError: '' });

    try {
      const res = await simpleFetch("/user/payments/addPayMe", "POST", {
        username: state.username,
        clientId: modalFormData.clientId,
        clientSecret: modalFormData.clientSecret,
        signingKeyId: modalFormData.signingKeyId,
        signingKey: modalFormData.signingKey,
        paymeTransactionFeePercentage: modalFormData.paymeTransactionFeePercentage
      });

      if (res && res.status === 200) {
        this.handleCloseModal();
        // Refresh organization data without full page reload
        if (func.loadOrganizationData) {
          func.loadOrganizationData();
        }
      } else {
        const serverMsg = res && res.body && res.body.message;
        this.setState({
          modalSaving: false,
          modalError: typeof serverMsg === 'string' && serverMsg ? serverMsg : saveFailedMsg
        });
      }
    } catch (error) {
      // console.error("Save failed:", error);
      this.setState({ modalSaving: false, modalError: saveFailedMsg });
    }
  };

  handleToggleStatus = (enabled, methodKey) => {
    const { func } = this.props;
    func.handleUpdatePaymentStatus({ target: { checked: enabled } }, methodKey);
  };

  handleFeeCoverageModeChange = (e) => {
    const newMode = e.target.value;
    this.setState({ savingFeeCoverageMode: true, feeCoverageModeStatus: 'saving' });

    simpleFetch("/organizations", "PUT", {
      feeCoverageMode: newMode,
    }).then((response) => {
      this.setState({ savingFeeCoverageMode: false, feeCoverageModeStatus: response.status === 200 ? 'saved' : '' });
      if (response.status === 200) {
        const { func } = this.props;
        if (func.loadOrganizationData) {
          func.loadOrganizationData();
        }
        setTimeout(() => this.setState({ feeCoverageModeStatus: '' }), 2000);
      }
    }).catch(() => {
      this.setState({ savingFeeCoverageMode: false, feeCoverageModeStatus: '' });
    });
  };

  handleToggleShowFeeCoverageAmount = (e) => {
    const newStatus = e.target.checked;
    this.setState({ savingShowFeeCoverageAmount: true });

    simpleFetch("/organizations", "PUT", {
      showFeeCoverageAmount: newStatus,
    }).then((response) => {
      this.setState({ savingShowFeeCoverageAmount: false });
      if (response.status === 200) {
        const { func } = this.props;
        if (func.loadOrganizationData) {
          func.loadOrganizationData();
        }
      }
    }).catch(() => {
      this.setState({ savingShowFeeCoverageAmount: false });
    });
  };

  handleToggleRequireTransactionId = (e) => {
    const newStatus = e.target.checked;
    this.setState({ savingRequireTransactionId: true });

    simpleFetch("/organizations", "PUT", {
      requireTransactionId: newStatus,
    }).then((response) => {
      this.setState({ savingRequireTransactionId: false });
      if (response.status === 200) {
        const { func } = this.props;
        if (func.loadOrganizationData) {
          func.loadOrganizationData();
        }
      }
    }).catch(() => {
      this.setState({ savingRequireTransactionId: false });
    });
  };

  renderModalContent() {
    const { selectedMethod } = this.state;
    const { state, func, languageObj } = this.props;

    if (!selectedMethod) return null;

    switch (selectedMethod.key) {
      case "stripe":
        return this.renderStripeContent();

      case "payme":
        return this.renderPayMeContent();

      case "fps":
        return <RenderFPS ref={this.childComponentRef} state={state} func={func} languageObj={languageObj} />;

      case "bank_transfer":
        return <RenderBankTransfer ref={this.childComponentRef} state={state} func={func} languageObj={languageObj} />;

      case "alipay":
        return RenderAlipay(state, func, languageObj);

      case "wechatpay":
        return RenderWeChatPay(state, func, languageObj);

      case "bocpay":
        return RenderBocPay(state, func, languageObj);

      case "octopus":
        return RenderOctopus(state, func, languageObj);

      case "check_writing":
        return <RenderCheckWriting ref={this.childComponentRef} state={state} func={func} />;

      case "cash":
        return <RenderCash ref={this.childComponentRef} state={state} func={func} />;

      case "paypal_manual":
        return <RenderPayPalManual ref={this.childComponentRef} state={state} func={func} />;

      case "paypal":
        return (
          <div className="RenderPaymentMethods-notImplemented">
            <p>PayPal integration coming soon.</p>
          </div>
        );

      case "tapandgo":
        return (
          <div className="RenderPaymentMethods-notImplemented">
            <p>Tap &amp; Go configuration is managed separately.</p>
          </div>
        );

      default:
        return (
          <div className="RenderPaymentMethods-notImplemented">
            <p>Edit form for {selectedMethod.label}</p>
          </div>
        );
    }
  }

  renderStripeContent() {
    const { state, func } = this.props;
    const languageObj = this.props.languageObj || {};

    return (
      <RenderStripe
        language={func.props.language}
        languageObj={languageObj.Stripe || {}}
        orgID={Number(state.orgID)}
        bankAc={state.bankAc}
        enableStripeLink={state.organizationObj?.enableStripeLink !== false}
        onToggleEnableStripeLink={func.handleToggleEnableStripeLink}
        loading={state.loading}
      />
    );
  }

  renderPayMeContent() {
    const { modalFormData, modalError } = this.state;
    const { languageObj } = this.props;
    const pmText = languageObj?.PaymentMethods || {};
    const pt = (key, fallback) => pmText[key] || fallback;

    return (
      <div className="RenderPaymentMethods-formContent">
        {modalError && (
          <div className="RenderPaymentMethods-saveError" role="alert">
            {modalError}
          </div>
        )}
        <div className="RenderPaymentMethods-formGroup">
          <label className="RenderPaymentMethods-label">{pt("clientId", "Client ID")}</label>
          <input
            type="text"
            name="clientId"
            className="RenderPaymentMethods-input"
            placeholder={pt("clientId", "Client ID")}
            value={modalFormData.clientId || ""}
            onChange={this.handleInputChange}
          />
        </div>

        <div className="RenderPaymentMethods-formGroup">
          <label className="RenderPaymentMethods-label">{pt("clientSecret", "Client Secret")}</label>
          <input
            type="password"
            name="clientSecret"
            className="RenderPaymentMethods-input"
            placeholder={pt("clientSecret", "Client Secret")}
            value={modalFormData.clientSecret || ""}
            onChange={this.handleInputChange}
          />
        </div>

        <div className="RenderPaymentMethods-formGroup">
          <label className="RenderPaymentMethods-label">{pt("signingKeyId", "Signing Key ID")}</label>
          <input
            type="text"
            name="signingKeyId"
            className="RenderPaymentMethods-input"
            placeholder={pt("signingKeyId", "Signing Key ID")}
            value={modalFormData.signingKeyId || ""}
            onChange={this.handleInputChange}
          />
        </div>

        <div className="RenderPaymentMethods-formGroup">
          <label className="RenderPaymentMethods-label">{pt("signingKey", "Signing Key")}</label>
          <input
            type="password"
            name="signingKey"
            className="RenderPaymentMethods-input"
            placeholder={pt("signingKey", "Signing Key")}
            value={modalFormData.signingKey || ""}
            onChange={this.handleInputChange}
          />
        </div>

        <div className="RenderPaymentMethods-formGroup">
          <label className="RenderPaymentMethods-label">{pt("transactionFee", "Transaction Fee Percentage")}</label>
          <div className="RenderPaymentMethods-feeInputContainer">
            <input
              type="number"
              name="paymeTransactionFeePercentage"
              className="RenderPaymentMethods-input RenderPaymentMethods-feeInput"
              placeholder="1.5"
              step="0.01"
              min="0"
              max="100"
              value={modalFormData.paymeTransactionFeePercentage || ""}
              onChange={this.handleInputChange}
            />
            <span className="RenderPaymentMethods-feePercent">%</span>
          </div>
          <div className="RenderPaymentMethods-feeHelp">
            {pt("transactionFeeHelp", "This fee is used to calculate cover fees when donors choose to cover transaction costs. Default: 1.5%")}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { state, func, languageObj } = this.props;
    const { modalOpen, selectedMethod, isEditMode } = this.state;

    if (state.loading) {
      return (
        <div className="RenderPaymentMethods-loading">
          <div className="RenderPaymentMethods-spinner" />
        </div>
      );
    }

    const textObj = languageObj?.PaymentMethods || {};
    const t = (key, fallback) => textObj[key] || fallback;

    return (
      <div className="RenderPaymentMethods-container">
        <div className="RenderPaymentMethods-header">
          <h1 className="RenderPaymentMethods-title">
            {t("title", "Payment Methods")}
          </h1>
        </div>

        <div className="RenderPaymentMethods-sectionsWrapper">
          {/* Automatic Payments Section */}
          <section className="RenderPaymentMethods-section">
            <h2 className="RenderPaymentMethods-sectionTitle">
              {t("automaticPayments", "Automatic Payments via APIs")}
            </h2>
            <p className="RenderPaymentMethods-sectionDescription">
              {t("automaticPaymentsDesc", "Payments are processed automatically with real-time confirmation")}
            </p>

            <div className="RenderPaymentMethods-cardGrid">
              {PAYMENT_METHODS_CONFIG.automatic.map((method) => {
                const status = this.getMethodStatus(method);
                const isComingSoon = method.key === "paypal" || method.key === "tapandgo";
                return (
                  <PaymentMethodCard
                    key={method.key}
                    config={method}
                    isEnabled={status.isEnabled}
                    hasData={status.hasData}
                    isConfigured={status.isConfigured}
                    requiresUpgrade={status.requiresUpgrade}
                    actionRequired={status.actionRequired}
                    onEdit={this.handleOpenModal}
                    onUpgrade={func.redirectToOrgBilling}
                    loading={state.loading || status.isLoading}
                    comingSoon={isComingSoon}
                    textObj={textObj}
                  />
                );
              })}
            </div>
            {(() => {
              const currentMode = (state.organizationObj && state.organizationObj.feeCoverageMode) || 'optional';
              const showAmountToggleVisible = currentMode !== 'disabled';
              return (
                <div className="RenderPaymentMethods-feeCoverageCard">
                  <div className="RenderPaymentMethods-feeCoverageHeader">
                    <div className="RenderPaymentMethods-settingLabel">
                      {t("feeCoverageMode", "Fee Coverage")}
                    </div>
                    <div className="RenderPaymentMethods-settingDescription">
                      {t("feeCoverageModeDesc", "Control how fee coverage is applied to all online payments for your organisation.")}
                    </div>
                  </div>
                  <div className="RenderPaymentMethods-feeCoverageRow">
                    <div className="RenderPaymentMethods-feeCoverageRowLabel">
                      {t("feeCoverageModeRowLabel", "Mode")}
                    </div>
                    <div className="RenderPaymentMethods-modeSelectWrap">
                      <select
                        className="RenderPaymentMethods-modeSelect"
                        value={currentMode}
                        onChange={this.handleFeeCoverageModeChange}
                        disabled={this.state.savingFeeCoverageMode}
                      >
                        <option value="optional">{t("feeCoverageModeOptional", "Optional — donor can opt in")}</option>
                        <option value="required">{t("feeCoverageModeRequired", "Required — always applied")}</option>
                        <option value="disabled">{t("feeCoverageModeDisabled", "Disabled — never applied")}</option>
                      </select>
                      {this.state.feeCoverageModeStatus === 'saving' && (
                        <span className="RenderPaymentMethods-modeStatus">{t("saving", "Saving...")}</span>
                      )}
                      {this.state.feeCoverageModeStatus === 'saved' && (
                        <span className="RenderPaymentMethods-modeStatus RenderPaymentMethods-modeStatus--saved">{t("saved", "Saved")}</span>
                      )}
                    </div>
                  </div>
                  {showAmountToggleVisible && (
                    <>
                      <div className="RenderPaymentMethods-feeCoverageDivider" />
                      <div className="RenderPaymentMethods-feeCoverageRow">
                        <div className="RenderPaymentMethods-feeCoverageRowInfo">
                          <div className="RenderPaymentMethods-settingLabel">
                            {t("showFeeCoverageAmount", "Show Fee Amount to Donors")}
                          </div>
                          <div className="RenderPaymentMethods-settingDescription">
                            {t("showFeeCoverageAmountDesc", "When enabled, donors will see an estimated fee amount when fee coverage is applied.")}
                          </div>
                        </div>
                        <ToggleSwitch
                          checked={!!(state.organizationObj && state.organizationObj.showFeeCoverageAmount)}
                          onChange={this.handleToggleShowFeeCoverageAmount}
                          disabled={this.state.savingShowFeeCoverageAmount}
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </section>

          {/* Manual Payments Section */}
          <section className="RenderPaymentMethods-section">
            <h2 className="RenderPaymentMethods-sectionTitle">
              {t("manualPayments", "Manual Payments")}
            </h2>
            <p className="RenderPaymentMethods-sectionDescription">
              {t("manualPaymentsDesc", "Donors select these options and follow your instructions")}
            </p>

            <div className="RenderPaymentMethods-settingRow">
              <div className="RenderPaymentMethods-settingInfo">
                <div className="RenderPaymentMethods-settingLabel">
                  {t("requireTransactionId", "Require Transaction ID")}
                </div>
                <div className="RenderPaymentMethods-settingDescription">
                  {t("requireTransactionIdDesc", "When enabled, donors must enter a transaction ID or receipt number when submitting manual payments")}
                </div>
              </div>
              <ToggleSwitch
                checked={!!(state.organizationObj && state.organizationObj.requireTransactionId)}
                onChange={this.handleToggleRequireTransactionId}
                disabled={this.state.savingRequireTransactionId}
              />
            </div>

            <div className="RenderPaymentMethods-cardGrid">
              {PAYMENT_METHODS_CONFIG.manual.map((method) => {
                const status = this.getMethodStatus(method);
                return (
                  <PaymentMethodCard
                    key={method.key}
                    config={method}
                    isEnabled={status.isEnabled}
                    hasData={status.hasData}
                    isConfigured={status.isConfigured}
                    requiresUpgrade={status.requiresUpgrade}
                    onEdit={this.handleOpenModal}
                    onUpgrade={func.redirectToOrgBilling}
                    loading={state.loading || status.isLoading}
                  />
                );
              })}
            </div>
          </section>
        </div>

        {/* Edit Modal */}
        <PaymentMethodModal
          isOpen={modalOpen}
          onClose={this.handleCloseModal}
          title={selectedMethod?.label || t("edit", "Edit")}
          size={selectedMethod?.key === "stripe" ? "large" : "default"}
          methodKey={selectedMethod?.key}
          onExpand={this.handleExpandToPage}
          showFooter={["fps", "bank_transfer", "check_writing", "cash", "paypal_manual", "payme"].includes(selectedMethod?.key)}
          isEditMode={isEditMode}
          alwaysEditable={selectedMethod?.key === "payme"}
          saving={this.state.modalSaving}
          onEdit={this.handleModalEdit}
          onSave={selectedMethod?.key === "payme" ? this.handlePayMeSave : this.handleModalSave}
          onCancel={this.handleModalCancel}
          textObj={textObj}
        >
          {this.renderModalContent()}
        </PaymentMethodModal>
      </div>
    );
  }
}

RenderPaymentMethods.propTypes = {
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  languageObj: PropTypes.object
};

export default RenderPaymentMethods;
